import { Trans } from "next-i18next"

import { Analytics, SharedInteractionEvents } from "@bounce/analytics"
import { formatStripeAmount, useTranslation } from "@bounce/i18n"
import { Button } from "@bounce/web-components"

import { HeroContainer } from "./HeroContainer"
import type { Interpolations } from "../hooks/useInterpolations"
import SafeLink from "@/components/Generic/SafeLink/SafeLink"
import { useStickyCTA } from "@/contexts/StickyCTAContext"
import type { StoreData } from "@/libs/bounce/stores/types"

import LocationImage from "@bounce/assets/icons/LocationPin.svg"

export const Hero = ({
  store,
  title,
  bookNowUrl,
  interpolations,
}: {
  store: StoreData
  title: string
  bookNowUrl: string
  interpolations: Interpolations
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation("marketing-ls-store")
  const { ref: stickyCTARef } = useStickyCTA()

  const { lowestPrice, city } = store

  return (
    <HeroContainer store={store} interpolations={interpolations}>
      <div className="flex items-start">
        <LocationImage className="mr-2 size-5 text-primary md:size-6" />
        <p className="label2 font-light">
          <Trans
            t={t}
            i18nKey="screen.storeScreen.hero.label"
            defaults="Luggage storage in <a>{{CITY_NAME}}</a>"
            components={{ a: <SafeLink href={`/city/${city.slug}`} /> }}
            values={{ CITY_NAME: city.translatedName }}
          />
        </p>
      </div>
      <h1 className="title1 mt-4">{title}</h1>
      <h2 className="font-sans-next mt-4 max-w-[490px] text-base !font-light leading-normal md:mt-6 md:text-2xl">
        <Trans
          t={t}
          i18nKey="screen.storeScreen.hero.subtitleV2"
          defaults="Don’t drag your bags around. Store your luggage from <bold>{{lowestPriceText}}/day</bold> and explore freely."
          components={{
            bold: <span className="font-semibold text-primary" />,
          }}
          values={{
            lowestPriceText: formatStripeAmount(lowestPrice, city.currency, language),
          }}
        />
      </h2>
      <div ref={stickyCTARef} className="mt-8 flex w-full flex-row md:mt-12">
        <Button
          href={bookNowUrl}
          rel="nofollow"
          onClick={() =>
            Analytics.track(SharedInteractionEvents.CTAPressed, {
              cta: "StoreScreenHeroPrimary",
            })
          }>
          {t("screen.storeScreen.hero.primaryButton", "Book now")}
        </Button>
      </div>
    </HeroContainer>
  )
}
